/**
 * DashboardMyKPIs — Personal KPI dashboard.
 *
 * Shows: cycle info, summary cards (on track/at risk/behind), overall score,
 * and a list of assigned KPIs with progress.
 * If no cycle exists, guides the user to create one.
 */
import React, { useEffect } from 'react';
import Target from 'lucide-react/dist/esm/icons/target';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import Clock from 'lucide-react/dist/esm/icons/clock';
import Check from 'lucide-react/dist/esm/icons/check';
import CalendarRange from 'lucide-react/dist/esm/icons/calendar-range';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import { Button, Spinner } from '../../../components/ui';
import { useKPIStore } from '../../../stores/kpiStore';
import { useUIStore } from '../../../stores/uiStore';
import { getTranslation } from '../../../i18n';
import KPIAssignmentCard from './KPIAssignmentCard';

interface DashboardMyKPIsProps {
  onSelectAssignment: (id: string) => void;
  onCheckIn?: (id: string) => void;
  onNavigateToCycles?: () => void;
}

const SummaryCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <div className={`flex items-center gap-3 p-3 rounded-xl border ${color}`}>
    <div className="shrink-0">{icon}</div>
    <div>
      <div className="text-lg font-bold text-content tabular-nums">{value}</div>
      <div className="text-[11px] text-content-tertiary uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

const DashboardMyKPIs: React.FC<DashboardMyKPIsProps> = ({ onSelectAssignment, onCheckIn, onNavigateToCycles }) => {
  const {
    myDashboard, dashboardLoading, fetchMyDashboard, activeCycleId, cycles,
  } = useKPIStore();
  const { language } = useUIStore();
  const t = (key: string) => getTranslation(language, key);

  useEffect(() => {
    fetchMyDashboard();
  }, [activeCycleId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner variant="success" />
      </div>
    );
  }

  if (cycles.length === 0) {
    return (
      <div className="text-center py-16 max-w-lg mx-auto animate-fade-in">
        <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-brand/10 border border-brand/20 flex items-center justify-center">
          <CalendarRange size={28} className="text-brand/60" />
        </div>
        <h3 className="text-base font-semibold text-content mb-2">{t('perfMyKpisGetStartedTitle')}</h3>
        <p className="text-sm text-content-muted mb-6 leading-relaxed">
          {t('perfMyKpisGetStartedDesc')}
        </p>
        {onNavigateToCycles ? (
          <Button
            variant="primary"
            size="lg"
            onClick={onNavigateToCycles}
            leftIcon={<CalendarRange size={16} />}
            rightIcon={<ArrowRight size={14} />}
          >
            {t('perfMyKpisCreateFirstCycle')}
          </Button>
        ) : (
          <p className="text-xs text-content-muted">{t('perfMyKpisAskAdminToCreateCycle')}</p>
        )}
      </div>
    );
  }

  if (!myDashboard || !myDashboard.cycle) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <CalendarRange size={36} className="text-content-faint mx-auto mb-4" />
        <h3 className="text-base font-semibold text-content-secondary mb-2">{t('perfMyKpisNoActiveCycleTitle')}</h3>
        <p className="text-sm text-content-tertiary max-w-md mx-auto">
          {t('perfMyKpisNoActiveCycleDesc')}
        </p>
      </div>
    );
  }

  const { cycle, assignments, summary, overall_score } = myDashboard;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Cycle Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-content">{cycle.name}</h2>
          <p className="text-xs text-content-tertiary mt-0.5">
            {new Date(cycle.start_date).toLocaleDateString()} — {new Date(cycle.end_date).toLocaleDateString()}
          </p>
        </div>
        {overall_score !== null && (
          <div className="text-right">
            <div className="text-2xl font-bold text-content tabular-nums">{Math.round(overall_score)}%</div>
            <div className="text-[11px] text-content-tertiary uppercase tracking-wider">{t('perfMyKpisOverallScore')}</div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <SummaryCard
            label={t('perfMyKpisSummaryOnTrack')}
            value={summary.on_track}
            icon={<TrendingUp size={18} className="text-emerald-400" />}
            color="bg-emerald-400/[0.07] border-emerald-400/15"
          />
          <SummaryCard
            label={t('perfMyKpisSummaryAtRisk')}
            value={summary.at_risk}
            icon={<AlertTriangle size={18} className="text-amber-400" />}
            color="bg-amber-400/[0.07] border-amber-400/15"
          />
          <SummaryCard
            label={t('perfMyKpisSummaryBehind')}
            value={summary.behind}
            icon={<TrendingDown size={18} className="text-status-error" />}
            color="bg-status-error/[0.07] border-status-error/15"
          />
          <SummaryCard
            label={t('perfMyKpisSummaryNotStarted')}
            value={summary.not_started}
            icon={<Clock size={18} className="text-content-muted" />}
            color="bg-surface-primary border-subtle"
          />
          <SummaryCard
            label={t('perfMyKpisSummaryCompleted')}
            value={summary.completed}
            icon={<Check size={18} className="text-blue-400" />}
            color="bg-blue-400/[0.07] border-blue-400/15"
          />
        </div>
      )}

      {/* KPI Cards — 2-column grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {assignments.length > 0 ? (
          assignments.map((a) => (
            <KPIAssignmentCard
              key={a.id}
              assignment={a}
              onClick={onSelectAssignment}
              onCheckIn={onCheckIn}
            />
          ))
        ) : (
          <div className="text-center py-12 col-span-full">
            <Target size={28} className="text-content-faint mx-auto mb-3" />
            <p className="text-sm text-content-tertiary mb-1">{t('perfMyKpisNoKpisAssigned')}</p>
            <p className="text-xs text-content-muted">{t('perfMyKpisNoKpisDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DashboardMyKPIs);
